import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
  createQueryRequestParams,
  getQueryUrlFromData,
} from '@/common/utils/transformFilters'
import { HYDRATE } from 'next-redux-wrapper'
import { open } from '@/store/modalsSlice'
import { LIMIT_MODAL } from '@/common/constants/modals'
import { LIMIT_CODE } from '@/common/constants/responseCodes'
import { searchInitialState } from '@/store/states/SearchInitialState'
import {
  resetAllFilters,
  resetFilterToDefaultByInputs,
  resetToDefaultFilter,
  setCurrentLayoutType,
  setFiltersParams,
  setIsInfiniteScroll,
  setIsLoadingItems,
  setItems,
  setPagesMap,
  setSelectedItem,
  updateFiltersParams,
  updatePaginationParams,
} from '@/store/reducers/SearchReducers'
import {
  fetchFiltersConfigPending,
  fetchFiltersConfigReject,
  fetchFiltersConfigSuccess,
  fetchItemsPending,
  fetchItemsRejected,
  fetchItemsSuccess,
  searchHydrate,
} from '@/store/actions/searchActions'
import { fetchFiltersConfig } from '@/store/thunks/searchThunks'
import { SEARCH_ADS } from '@/common/constants/search'
import {
  APPLIED_CREATIVES_FILTERS,
  FILTERS_ADDITIONAL_OPTIONS,
  FILTERS_ADDITIONAL_OPTIONS_NAMES,
  FILTERS_PARAMS_TYPES,
  SEARCH_BY_TEXT_MAP,
} from '@/common/constants/filtersFields'
import { setCookie } from 'cookies-next'
import { fetchCreative } from '@/store/creativesSlice'

export const fetchAds = createAsyncThunk(
  'creatives/fetchCreatives',
  async (
    {
      withInfiniteScroll = false,
      request,
      params = {},
      itemsName = 'advertisements',
      isUpdateCookieFilters,
      signal,
    },
    { rejectWithValue, dispatch, getState }
  ) => {
    try {
      if (!withInfiniteScroll) {
        const { setAds } = adsSlice.actions
        dispatch(setAds([]))
      }

      const filters = getState().ads.filtersParams

      const queryMap = {
        ...params,
        ...createQueryRequestParams(filters),
      }
      const query = getQueryUrlFromData(queryMap)

      if (isUpdateCookieFilters) {
        setCookie(APPLIED_CREATIVES_FILTERS, query)
      }

      const { creativeIdentical } = query
      const { [itemsName]: items, pagination } = await request(queryMap, signal)

      const modifiedCreatives = items.map((item) => ({
        ...item,
        isTranslate: false,
      }))

      return {
        items: modifiedCreatives,
        pagination: pagination || { pages: {}, results: {} },
        withInfiniteScroll: withInfiniteScroll,
        isVisibleFilters: !creativeIdentical,
      }
    } catch (e) {
      const headers = e?.response?.headers ?? {}
      const error = e.data?.error ?? {}
      const response = e.response ?? {}

      if (error?.code === LIMIT_CODE) {
        await dispatch(open(LIMIT_MODAL))
      }

      return rejectWithValue({ ...headers, ...error, ...response })
    }
  }
)

export const fetchAd = createAsyncThunk(
  'ads/fetchAd',
  async ({ params, request }, { rejectWithValue }) => {
    try {
      const data = await request(params)
      return data
    } catch (e) {
      return rejectWithValue(e)
    }
  }
)

export const adsSlice = createSlice({
  name: 'ads',
  initialState: {
    ...searchInitialState,
    isAdsFiltersOpen: true,
    isAdsDetailsLoading: false,
    isVisibleBlackListModal: false,
    currentSlideData: null,
    needFetchAds: true,
    favoriteGroupsMap: {
      pagination: null,
      groups: [],
    },
    selectedActiveChip: null,
    activeVideo: null,
    prevActiveVideo: null,
    isCoverage: false,
    isActiveBlackList: false,
  },
  reducers: {
    setIsActiveBlackListAds(state, { payload }) {
      state.isActiveBlackList = payload
    },
    openAdsFilters(state) {
      state.isAdsFiltersOpen = true
    },
    closeAdsFilters(state) {
      state.isAdsFiltersOpen = false
    },
    updateSearchBarFilters(state, { payload }) {
      Object.keys(payload).forEach((key) => {
        const filter = `${key}_search`
        state.filtersParams[filter] = {
          ...state.filtersParams[filter],
          value: payload[key],
        }
      })
    },
    setLayoutTypeAds: setCurrentLayoutType,
    setFavoriteGroupsMap(state, { payload }) {
      state.favoriteGroupsMap = payload
    },
    setIsVisibleBlackListAdsModal(state, { payload }) {
      state.isVisibleBlackListModal = payload
    },
    setIsLoadingAds: setIsLoadingItems,
    setIsActiveInfiniteScrollAds: setIsInfiniteScroll,
    setIsFetchAds(state, { payload }) {
      state.needFetchAds = payload
    },
    setCurrentSlideDataAds(state, { payload }) {
      state.currentSlideData = payload
    },
    updatePaginationParamsAds: updatePaginationParams,
    setAds: setItems,
    setPagesMapAds: setPagesMap,
    setSelectedAd: setSelectedItem,
    setIsCoverageAds(state, { payload }) {
      state.isCoverage = payload
    },
    setSelectedActiveChip(state, { payload }) {
      state.selectedActiveChip = payload
    },
    setActiveVideoAds(state, { payload }) {
      state.prevActiveVideo = state.activeVideo
      state.activeVideo = payload
    },
    setIsLoadingByPagination(state, { payload }) {
      state.isLoadingByPagination = payload
    },
    updateFiltersParamsAds: updateFiltersParams,
    setFiltersParamsAds: setFiltersParams,
    resetToDefaultFilterAds: resetToDefaultFilter,
    resetFilterToDefaultByInputsAds: resetFilterToDefaultByInputs,
    resetAllFiltersAds: resetAllFilters,
    resetLeftFiltersNoSearch(state) {
      if (state.filtersParams) {
        Object.keys(state.filtersParams).forEach((key) => {
          if (FILTERS_ADDITIONAL_OPTIONS_NAMES[key]) {
            state.filtersParams[key] = FILTERS_ADDITIONAL_OPTIONS[key]
            return
          }

          if (SEARCH_BY_TEXT_MAP[key]) return

          state.filtersParams[key] =
            FILTERS_PARAMS_TYPES[state.filtersConfigMap[key].type]
        })
      }
    },
    setFilters(state, action) {
      fetchFiltersConfigSuccess(state, action)
    },
  },
  extraReducers: {
    [HYDRATE]: (state, action) => {
      searchHydrate({ state, action, slice: 'ads' })
    },
    [fetchAds.pending]: fetchItemsPending,
    [fetchAds.fulfilled]: fetchItemsSuccess,
    [fetchAds.rejected]: fetchItemsRejected,
    [fetchCreative.pending]: (state) => {
      state.isCreativeDetailsLoading = true
    },
    [fetchCreative.fulfilled]: (state, { payload }) => {
      state.isCreativeDetailsLoading = false
      state.selectedItem = payload
    },
    [fetchCreative.rejected]: (state) => {
      state.isCreativeDetailsLoading = false
    },
    [fetchFiltersConfig.pending]: (state, action) => {
      if (action.payload === SEARCH_ADS) {
        fetchFiltersConfigPending(state, action)
      }
    },
    [fetchFiltersConfig.fulfilled]: (state, action) => {
      if (action.payload.sliceName === SEARCH_ADS) {
        fetchFiltersConfigSuccess(state, action)
      }
    },
    [fetchFiltersConfig.rejected]: (state, action) => {
      if (action.payload === SEARCH_ADS) {
        fetchFiltersConfigReject(state, action)
      }
    },
  },
})

export const adsActions = adsSlice.actions

export default adsSlice.reducer
